// FILE: src/pages/admin/AdminResourceDetailPage.jsx
// One lesson (WP-5; R1, matrix 9.7, 0.9, 11.2): what it is, its curriculum,
// who uses it (students with progress, learning sessions, lessons that
// belong to it), its slides with CC codes, and its videos. From here an
// admin edits the details or the slides, or deletes it. A delete the server
// refuses (409 RESOURCE_IN_USE) is explained with the counts; a lesson of an
// approved curriculum is frozen (CURRICULUM_LOCKED: make a new version).
//
//   GET    /api/admin/resources/:id   + curriculum { id, name, version, status }, usage
//   DELETE /api/admin/resources/:id
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Link,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ViewCarouselOutlinedIcon from "@mui/icons-material/ViewCarouselOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import fetchWithAuth from "../../utils/fetchWithAuth";
import { DetailList, EmptyState, ErrorState, LoadingState, PageHeader, Section } from "../../components/ui";
import { ResponsiveTable } from "../../components/admin/AdminBits";
import { ActionBar, ConfirmDialog, CurriculumStatusChip, PracticeChip } from "../../components/admin/ContentBits";
import { curriculumName, formatSeconds, passMarkText, slideTotals } from "../../components/admin/content";
import { errorPayload } from "../../components/admin/apiError";
import { formatDateTime } from "../../components/admin/format";
import { kindText } from "../AdminResourceListPage";
import { formatDuration } from "../../utils/timeFormat";
import { errorCode, messageFor } from "../../i18n/errors";
import { challengeTypeLabel } from "../../i18n/labels";
import { useCan } from "../../hooks/usePermissions";

const LIST = "/admin/resources";
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** Who depends on a lesson, in words: "2 students have progress on it; 5 learning sessions; …". */
export function usageText(usage) {
  if (!usage) return "";
  const parts = [];
  const students = Number(usage.students) || 0;
  const sessions = Number(usage.sessions) || 0;
  const dependents = Array.isArray(usage.dependents) ? usage.dependents : [];
  if (students) parts.push(`${plural(students, "student has", "students have")} progress on it`);
  if (sessions) parts.push(`${plural(sessions, "learning session was", "learning sessions were")} recorded on it`);
  if (dependents.length) parts.push(`${dependents.map((d) => d.title).join(", ")} ${dependents.length === 1 ? "belongs" : "belong"} to it`);
  return parts.length ? `${parts.join("; ")}.` : "";
}

export default function AdminResourceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState({ resource: null, loading: true, error: null });
  const [parent, setParent] = useState(null);
  const [notice, setNotice] = useState(location.state?.notice || "");
  const [confirm, setConfirm] = useState({ open: false, busy: false, error: null });
  const latest = useRef(0);
  const canEdit = useCan("content.edit");

  const load = useCallback(() => {
    const req = ++latest.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    fetchWithAuth(`/api/admin/resources/${encodeURIComponent(id)}`)
      .then((resource) => {
        if (req === latest.current) setState({ resource, loading: false, error: null });
      })
      .catch((error) => {
        if (req === latest.current) setState({ resource: null, loading: false, error: error || "INTERNAL_ERROR" });
      });
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const parentId = state.resource?.parentResourceId ? String(state.resource.parentResourceId) : null;
  useEffect(() => {
    if (!parentId) {
      setParent(null);
      return;
    }
    fetchWithAuth(`/api/admin/resources/${encodeURIComponent(parentId)}`)
      .then((p) => setParent(p))
      .catch(() => setParent(null));
  }, [parentId]);

  const { resource, loading, error } = state;

  if (!resource) {
    if (loading) return <LoadingState variant="page" label="Loading the lesson…" />;
    const missing = ["RESOURCE_NOT_FOUND", "NOT_FOUND"].includes(errorCode(error));
    return (
      <Box>
        <PageHeader title="Lesson" breadcrumbs={[{ label: "Lessons", to: LIST }, { label: "Lesson" }]} />
        {missing ? (
          <EmptyState
            icon={MenuBookOutlinedIcon}
            title={messageFor("RESOURCE_NOT_FOUND")}
            description="It may have been deleted. Go back to the lesson list and choose another."
            action={
              <Button component={RouterLink} to={LIST} variant="contained">
                Back to lessons
              </Button>
            }
          />
        ) : (
          <ErrorState error={error} title="We couldn't load this lesson" onRetry={load} />
        )}
      </Box>
    );
  }

  const title = resource.title || "Untitled lesson";
  const curriculum = resource.curriculum;
  const frozen = !!curriculum && curriculum.status !== "DRAFT";
  // Changing lessons needs content.edit (RBAC); without it the page is read only.
  const readOnly = frozen || !canEdit;
  const slides = Array.isArray(resource.slides) ? resource.slides : [];
  const media = Array.isArray(resource.media) ? resource.media : [];
  const usage = resource.usage || { students: 0, sessions: 0, dependents: [] };
  const totals = slideTotals(slides);

  const doDelete = async () => {
    setConfirm((c) => ({ ...c, busy: true, error: null }));
    try {
      await fetchWithAuth(`/api/admin/resources/${encodeURIComponent(resource._id)}`, { method: "DELETE" });
      navigate(LIST, { state: { notice: `${title} was deleted.` } });
    } catch (err) {
      setConfirm({ open: true, busy: false, error: err || "INTERNAL_ERROR" });
      if (errorCode(err) === "RESOURCE_IN_USE") {
        const body = errorPayload(err);
        setState((s) => ({ ...s, resource: { ...s.resource, usage: { students: body.students, sessions: body.sessions, dependents: body.dependents || [] } } }));
      }
    }
  };

  const refusal = confirm.error && errorCode(confirm.error) === "RESOURCE_IN_USE" ? usageText(errorPayload(confirm.error)) : null;

  return (
    <Box>
      <PageHeader
        eyebrow={`${kindText(resource)}${resource.order ? ` ${resource.order}` : ""}`}
        title={title}
        breadcrumbs={[{ label: "Lessons", to: LIST }, { label: title }]}
        description={resource.description || undefined}
      >
        {!readOnly && (
          <ActionBar label="Lesson actions">
            <Button component={RouterLink} to={`/admin/resources/${resource._id}/edit`} variant="contained" startIcon={<EditOutlinedIcon />}>
              Edit details
            </Button>
            <Button component={RouterLink} to={`/admin/resources/${resource._id}/slides`} variant="outlined" startIcon={<ViewCarouselOutlinedIcon />}>
              Edit slides and videos
            </Button>
            <Button variant="outlined" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => setConfirm({ open: true, busy: false, error: null })}>
              Delete
            </Button>
          </ActionBar>
        )}
      </PageHeader>

      {notice && (
        <Alert severity="success" role="status" sx={{ mb: 2.5 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}
      {frozen && (
        <Alert severity="info" sx={{ mb: 2.5 }}>
          <AlertTitle>This lesson can't be changed or deleted</AlertTitle>
          It belongs to {curriculumName(curriculum)}, which is {curriculum.status === "RETIRED" ? "retired" : "approved"}. Approved content stays
          exactly as MTO approved it. To change it, make a new version on the{" "}
          <Link component={RouterLink} to={`/admin/curricula/${curriculum.id}`}>
            curriculum's page
          </Link>
          .
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gap: 2.5,
          alignItems: "start",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.6fr) minmax(0, 1fr)" },
          mb: 2.5
        }}
      >
        <Section id="lesson-about" title="About this lesson">
          <DetailList
            items={[
              { label: "Type", value: kindText(resource) },
              { label: "Position", value: resource.order ?? "—" },
              { label: "Content file", value: resource.url || "None yet" },
              {
                label: "Parent chapter",
                value: parentId ? (
                  <Link component={RouterLink} to={`/admin/resources/${parentId}`}>
                    {parent?.title || "Open the parent chapter"}
                  </Link>
                ) : resource.resourceType === "HOMELINK" ? (
                  "None: open from the start"
                ) : (
                  "None"
                )
              },
              { label: "Practice activity", value: resource.isPractice ? <PracticeChip /> : "No: scored and in sequence" },
              { label: "Pass mark", value: resource.isPractice ? "Not scored" : passMarkText(resource.assessment?.passMark) },
              {
                label: "Learning outcomes (CC codes)",
                value: (resource.ilos || []).length ? resource.ilos.join(", ") : "None yet"
              },
              { label: "Last changed", value: formatDateTime(resource.updatedAt) }
            ]}
          />
        </Section>

        <Stack spacing={2.5}>
          <Section id="lesson-curriculum" title="Curriculum">
            {curriculum ? (
              <Stack spacing={1.5}>
                <Link component={RouterLink} to={`/admin/curricula/${curriculum.id}`} sx={{ fontWeight: 600 }}>
                  {curriculumName(curriculum)}
                </Link>
                <Box>
                  <CurriculumStatusChip status={curriculum.status} />
                </Box>
              </Stack>
            ) : (
              <Typography sx={{ color: "text.secondary" }}>
                Not in a curriculum. It was added before curricula existed; the content migration puts it in the approved curriculum.
              </Typography>
            )}
          </Section>

          <Section id="lesson-usage" title="Who uses it" description="A lesson can only be deleted when nobody depends on it.">
            <DetailList
              columns={1}
              items={[
                { label: "Students with progress", value: String(usage.students ?? 0) },
                { label: "Learning sessions", value: String(usage.sessions ?? 0) },
                {
                  label: "Lessons that belong to it",
                  value: (usage.dependents || []).length ? (
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                      {usage.dependents.map((d) => (
                        <li key={d.id}>
                          <Link component={RouterLink} to={`/admin/resources/${d.id}`}>
                            {d.title}
                          </Link>
                        </li>
                      ))}
                    </Box>
                  ) : (
                    "None"
                  )
                }
              ]}
            />
          </Section>
        </Stack>
      </Box>

      <Stack spacing={2.5}>
        <Section id="lesson-slides" title="Slides" description={totals.text}>
          {slides.length === 0 ? (
            <EmptyState
              compact
              title="No slides yet"
              description={readOnly ? "This lesson has no slides." : "Add the slides from the deck's publish list on the slides page."}
              action={
                readOnly ? null : (
                  <Button component={RouterLink} to={`/admin/resources/${resource._id}/slides`} variant="contained">
                    Add slides
                  </Button>
                )
              }
            />
          ) : (
            <ResponsiveTable label="Slides" minWidth={680}>
              <TableHead>
                <TableRow>
                  <TableCell component="th" scope="col" align="right">
                    Slide
                  </TableCell>
                  <TableCell component="th" scope="col">
                    Name
                  </TableCell>
                  <TableCell component="th" scope="col" align="right">
                    Time
                  </TableCell>
                  <TableCell component="th" scope="col">
                    Activity
                  </TableCell>
                  <TableCell component="th" scope="col">
                    CC code
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {slides.map((s, i) => (
                  <TableRow key={s._id || i}>
                    <TableCell align="right">{i + 1}</TableCell>
                    <TableCell component="th" scope="row">
                      {s.slideName}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      {formatSeconds(s.slideTime)}
                    </TableCell>
                    <TableCell>{challengeTypeLabel(s.challengeType || "NONE")}</TableCell>
                    <TableCell>{s.ccCode || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </ResponsiveTable>
          )}
        </Section>

        <Section id="lesson-videos" title="Videos" description="Each video must be 7 minutes or shorter, and an hour of lessons can have at most 20 minutes of video (MTO 3.5).">
          {media.length === 0 ? (
            <Typography sx={{ color: "text.secondary" }}>
              No videos entered. The curriculum check can only measure video length when each video's length is entered.
            </Typography>
          ) : (
            <ResponsiveTable label="Videos" minWidth={520}>
              <TableHead>
                <TableRow>
                  <TableCell component="th" scope="col">
                    Video
                  </TableCell>
                  <TableCell component="th" scope="col" align="right">
                    Length
                  </TableCell>
                  <TableCell component="th" scope="col" align="right">
                    On slide
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {media.map((m, i) => (
                  <TableRow key={`${m.title}-${i}`}>
                    <TableCell component="th" scope="row">
                      {m.title || `Video ${i + 1}`}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      {formatSeconds(m.durationSec)}
                    </TableCell>
                    <TableCell align="right">{m.slideIndex || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </ResponsiveTable>
          )}
          {resource.totalDuration > 0 && (
            <Typography sx={{ color: "text.secondary", mt: 2 }}>Lesson length: {formatDuration(resource.totalDuration)}</Typography>
          )}
        </Section>
      </Stack>

      <ConfirmDialog
        id="delete-lesson"
        open={confirm.open}
        icon={DeleteOutlineIcon}
        tone="error"
        title={`Delete ${title}?`}
        description="The lesson and its slides are removed from this draft curriculum. This can't be undone."
        confirmLabel="Delete lesson"
        busyLabel="Deleting…"
        confirmColor="error"
        busy={confirm.busy}
        error={confirm.error}
        errorDetail={refusal}
        onConfirm={doDelete}
        onClose={() => setConfirm({ open: false, busy: false, error: null })}
      />
    </Box>
  );
}
